import React, { useEffect, useMemo, useState } from 'react';

import { useNotification, useTranslation } from '@refinedev/core';
import { Dropzone, FileWithPath } from '@mantine/dropzone';
import {
  ActionIcon,
  Alert,
  Button,
  Card,
  Group,
  Loader,
  Modal,
  ScrollArea,
  Stack,
  Text,
  Textarea,
  ThemeIcon,
} from '@mantine/core';
import {
  IconAlertTriangle,
  IconCheck,
  IconFile,
  IconUpload,
  IconX,
} from '@tabler/icons-react';
import { useQueryClient } from '@tanstack/react-query';
import * as Sentry from '@sentry/react';
import { v4 as uuidv4 } from 'uuid';

import { ProviderSelect } from 'components/ProviderSelect';
import {
  createIntakeItem,
  getIntakeItem,
  intakeItemQueryKeys,
  useListProvidersBySite,
} from 'api/resource';
import { TaskStatus } from 'interfaces/task';
import { useCurrentSiteStore } from 'store/SiteStore';
import { useCurrentUserStore } from 'store/UserStore';
import { nowISO } from 'utils/dateUtils';
import { buildIntakeAttachmentKeys } from 'utils/intakeAttachmentKey';
import { uploadFileToS3 } from 'utils/fileUpload';
import { useIsProvider } from 'hooks/useIsProvider';

import { resolveCurrentUploadProviderID } from './resolveCurrentUploadProvider';

const ACCEPTED_UPLOAD_TYPES = {
  'application/pdf': ['.pdf'],
  'text/csv': ['.csv'],
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': [
    '.xlsx',
  ],
  'application/vnd.ms-excel.sheet.macroEnabled.12': ['.xlsm'],
  'image/png': ['.png'],
  'image/jpeg': ['.jpg', '.jpeg'],
  'image/gif': ['.gif'],
  'image/webp': ['.webp'],
  'image/tiff': ['.tif', '.tiff'],
  'image/bmp': ['.bmp'],
  'image/heic': ['.heic'],
  'image/heif': ['.heif'],
};
const ACCEPTED_UPLOAD_DESCRIPTION =
  'PDF, CSV, XLSX, XLSM, PNG, JPG, GIF, WEBP, TIFF, BMP, HEIC, and HEIF files are supported.';

const INTAKE_BUCKET = `intake-items-${import.meta.env.VITE_ACCOUNT_ID}`;

interface UploadCreateModalProps {
  opened: boolean;
  onClose: () => void;
}

interface PendingUploadSubmission {
  intakeItemID: string;
  uploadKeys: string[];
  uploaded: boolean;
}

const getFileKey = (file: File) =>
  `${file.name}-${file.size}-${file.lastModified}-${file.type}`;

export const UploadCreateModal: React.FC<UploadCreateModalProps> = ({
  opened,
  onClose,
}) => {
  const { translate } = useTranslation();
  const { open: openNotification } = useNotification();
  const queryClient = useQueryClient();
  const currentSite = useCurrentSiteStore();
  const isProvider = useIsProvider();
  const { providerID: currentUserProviderID, providers: currentUserProviders } =
    useCurrentUserStore();
  const { data: providers = [], isLoading: isLoadingProviders } =
    useListProvidersBySite({
      siteID: currentSite.id,
    });

  const [selectedProviderID, setSelectedProviderID] = useState<string | null>(
    null
  );
  const [notes, setNotes] = useState('');
  const [files, setFiles] = useState<FileWithPath[]>([]);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [pendingSubmission, setPendingSubmission] =
    useState<PendingUploadSubmission | null>(null);

  const providerOptions = useMemo(() => providers || [], [providers]);
  const resolvedProviderID = useMemo(
    () =>
      resolveCurrentUploadProviderID({
        providerID: currentUserProviderID,
        providers: currentUserProviders,
        siteID: currentSite.id,
      }),
    [currentSite.id, currentUserProviderID, currentUserProviders]
  );
  const resolvedProvider = useMemo(
    () =>
      providerOptions.find((provider) => provider.id === resolvedProviderID) ||
      currentUserProviders?.find(
        (provider) => provider.id === resolvedProviderID
      ) ||
      null,
    [currentUserProviders, providerOptions, resolvedProviderID]
  );
  const shouldHideProviderSelect = isProvider && !!resolvedProviderID;

  const resetState = () => {
    setSelectedProviderID(null);
    setNotes('');
    setFiles([]);
    setSubmitError(null);
    setIsSubmitting(false);
    setPendingSubmission(null);
  };

  useEffect(() => {
    if (!opened) {
      resetState();
    }
  }, [opened]);

  useEffect(() => {
    if (!opened) return;
    if (shouldHideProviderSelect && resolvedProviderID) {
      setSelectedProviderID(resolvedProviderID);
      return;
    }
    if (selectedProviderID) return;
    if (providerOptions.length !== 1) return;
    setSelectedProviderID(providerOptions[0].id);
  }, [
    opened,
    providerOptions,
    resolvedProviderID,
    selectedProviderID,
    shouldHideProviderSelect,
  ]);

  const handleClose = () => {
    resetState();
    onClose();
  };

  const handleDrop = (droppedFiles: FileWithPath[]) => {
    setPendingSubmission(null);
    setFiles((prev) => {
      const existing = new Map(prev.map((file) => [getFileKey(file), file]));
      droppedFiles.forEach((file) => {
        existing.set(getFileKey(file), file);
      });
      return Array.from(existing.values());
    });
  };

  const handleRemoveFile = (fileKey: string) => {
    setPendingSubmission(null);
    setFiles((prev) => prev.filter((file) => getFileKey(file) !== fileKey));
  };

  const canSubmit = Boolean(
    currentSite.id && selectedProviderID && files.length
  );

  const submitUploads = async () => {
    if (!currentSite.id || !selectedProviderID || !files.length) return;

    setIsSubmitting(true);
    setSubmitError(null);
    let currentSubmission = pendingSubmission;

    try {
      if (!currentSubmission) {
        const intakeItemID = uuidv4();
        const uploadKeys = buildIntakeAttachmentKeys({
          files,
          siteID: currentSite.id,
          providerID: selectedProviderID,
          intakeItemID,
        });
        currentSubmission = {
          intakeItemID,
          uploadKeys,
          uploaded: false,
        };
        setPendingSubmission(currentSubmission);
      }

      if (!currentSubmission.uploaded) {
        const uploadResults = await uploadFileToS3(
          files,
          INTAKE_BUCKET,
          currentSubmission.uploadKeys
        );
        const failedUploads = uploadResults.filter((result) => !result).length;

        if (failedUploads > 0) {
          setSubmitError(
            translate(
              'pages:uploads.createModal.uploadFailure',
              'One or more files failed to upload. No intake item was created. Retry to use the same upload keys.'
            )
          );
          return;
        }

        currentSubmission = {
          ...currentSubmission,
          uploaded: true,
        };
        setPendingSubmission(currentSubmission);
      }

      const intakeItemPayload = {
        id: currentSubmission.intakeItemID,
        siteID: currentSite.id,
        providerID: selectedProviderID,
        createdAt: nowISO(currentSite.timezone) || new Date().toISOString(),
        completedAt: null,
        status: TaskStatus.toDo,
        notes: notes.trim() ? notes.trim() : null,
        assigneeUserID: null,
      };

      const createdItem = await createIntakeItem(intakeItemPayload);

      if (!createdItem?.id) {
        throw new Error('Failed to create intake item');
      }

      await queryClient.invalidateQueries({
        queryKey: intakeItemQueryKeys.all,
      });

      openNotification?.({
        type: 'success',
        message: translate(
          'pages:uploads.createModal.success',
          'Upload created successfully.'
        ),
      });
      handleClose();
    } catch (error) {
      Sentry.captureException(error);

      if (currentSubmission?.uploaded) {
        await queryClient.invalidateQueries({
          queryKey: intakeItemQueryKeys.all,
        });

        try {
          const existingItem = await getIntakeItem(
            currentSubmission.intakeItemID
          );

          if (existingItem?.id) {
            openNotification?.({
              type: 'error',
              message: translate(
                'pages:uploads.createModal.finalizedWithWarning',
                'Upload created, but downstream processing reported an error. The uploads list has been refreshed.'
              ),
            });
            handleClose();
            return;
          }
        } catch (lookupError) {
          Sentry.captureException(lookupError);
        }
      }

      setSubmitError(
        translate(
          'pages:uploads.createModal.createFailure',
          'Files uploaded, but the upload could not be finalized. Retry to reuse the same upload data.'
        )
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal
      opened={opened}
      onClose={handleClose}
      size="lg"
      title={translate('pages:uploads.createModal.title', 'Create Upload')}
      data-testid="upload-create-modal"
    >
      <Stack gap="md">
        {submitError ? (
          <Alert
            color="red"
            icon={<IconAlertTriangle size={16} />}
            data-testid="upload-create-error"
          >
            {submitError}
          </Alert>
        ) : null}

        {shouldHideProviderSelect ? (
          <Stack gap={4} data-testid="upload-provider-display">
            <Text size="sm" fw={500}>
              {translate('pages:uploads.createModal.providerLabel', 'Provider')}
            </Text>
            <Text>
              {resolvedProvider?.firstName} {resolvedProvider?.lastName}
            </Text>
          </Stack>
        ) : (
          <ProviderSelect
            providers={providerOptions}
            value={selectedProviderID}
            onChange={(value) => {
              setPendingSubmission(null);
              setSelectedProviderID(value);
            }}
            label={translate(
              'pages:uploads.createModal.providerLabel',
              'Provider'
            )}
            placeholder={translate(
              'pages:uploads.createModal.providerPlaceholder',
              'Select provider'
            )}
            searchable
            clearable={false}
            disabled={isLoadingProviders}
            dataTestId="upload-provider-select"
          />
        )}

        <Textarea
          label={translate('pages:uploads.createModal.notesLabel', 'Notes')}
          placeholder={translate(
            'pages:uploads.createModal.notesPlaceholder',
            'Add notes for this upload'
          )}
          value={notes}
          onChange={(event) => setNotes(event.currentTarget.value)}
          minRows={3}
          data-testid="upload-notes-input"
        />

        <Dropzone
          onDrop={handleDrop}
          accept={ACCEPTED_UPLOAD_TYPES}
          multiple
          disabled={isSubmitting}
          loading={isSubmitting}
          data-testid="upload-create-dropzone"
        >
          <Stack align="center" gap="sm" py="lg">
            <Dropzone.Accept>
              <ThemeIcon size={52} radius="xl" color="green" variant="light">
                <IconCheck size={28} />
              </ThemeIcon>
            </Dropzone.Accept>
            <Dropzone.Reject>
              <ThemeIcon size={52} radius="xl" color="red" variant="light">
                <IconX size={28} />
              </ThemeIcon>
            </Dropzone.Reject>
            <Dropzone.Idle>
              <ThemeIcon size={52} radius="xl" color="blue" variant="light">
                <IconUpload size={28} />
              </ThemeIcon>
            </Dropzone.Idle>
            <Stack gap={2} align="center">
              <Text fw={600}>
                {translate(
                  'pages:uploads.createModal.dropzoneTitle',
                  'Drag files here or click to browse'
                )}
              </Text>
              <Text size="sm" c="dimmed">
                {translate(
                  'pages:uploads.createModal.dropzoneDescription',
                  ACCEPTED_UPLOAD_DESCRIPTION
                )}
              </Text>
            </Stack>
          </Stack>
        </Dropzone>

        <Card withBorder radius="md" p="sm">
          <Stack gap="xs">
            <Text fw={600}>
              {translate(
                'pages:uploads.createModal.selectedFiles',
                'Selected Files'
              )}
            </Text>
            {files.length ? (
              <ScrollArea.Autosize mah={220}>
                <Stack gap="xs">
                  {files.map((file) => {
                    const fileKey = getFileKey(file);
                    return (
                      <Group
                        key={fileKey}
                        justify="space-between"
                        wrap="nowrap"
                      >
                        <Group gap="sm" wrap="nowrap">
                          <ThemeIcon variant="light" color="gray">
                            <IconFile size={16} />
                          </ThemeIcon>
                          <Text size="sm" truncate>
                            {file.name}
                          </Text>
                        </Group>
                        <ActionIcon
                          variant="subtle"
                          color="red"
                          onClick={() => handleRemoveFile(fileKey)}
                          disabled={isSubmitting}
                          aria-label={translate(
                            'pages:uploads.createModal.removeFile',
                            'Remove file'
                          )}
                        >
                          <IconX size={16} />
                        </ActionIcon>
                      </Group>
                    );
                  })}
                </Stack>
              </ScrollArea.Autosize>
            ) : (
              <Text size="sm" c="dimmed">
                {translate(
                  'pages:uploads.createModal.noFiles',
                  'No files selected yet.'
                )}
              </Text>
            )}
          </Stack>
        </Card>

        {isSubmitting ? (
          <Group justify="center">
            <Loader size="sm" />
          </Group>
        ) : null}

        <Group justify="flex-end">
          <Button variant="default" onClick={handleClose}>
            {translate('pages:uploads.createModal.cancel', 'Cancel')}
          </Button>
          <Button
            onClick={submitUploads}
            disabled={!canSubmit || isSubmitting || isLoadingProviders}
            data-testid="upload-create-submit"
          >
            {translate(
              pendingSubmission
                ? 'pages:uploads.createModal.retry'
                : 'pages:uploads.createModal.submit',
              pendingSubmission ? 'Retry Upload' : 'Create Upload'
            )}
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
};
